import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AnalysisCard from '../components/AnalysisCard';
import Icon from '../design-system/primitives/Icon';
import ThemeToggle from '../design-system/primitives/ThemeToggle';
import { apiService } from '../services/api';
import './StartPage.css';

const DEFAULT_USER_IMAGE = '/user.svg';

const Wordmark = ({ size = 18, onClick }) => (
  <span
    onClick={onClick}
    style={{
      display: 'inline-flex', alignItems: 'center',
      color: 'var(--fg-strong)', lineHeight: 1,
      fontFamily: 'var(--font-sans)',
      fontWeight: 600, fontSize: size, letterSpacing: '-0.025em',
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    Qombra
  </span>
);

const AccountButton = ({ user, onClick }) => (
  <button className="app-account-btn" onClick={onClick}>
    <span className="app-account-btn__avatar">
      <img src={user?.image_url || DEFAULT_USER_IMAGE} alt="" />
      {user?.org_image_url && (
        <img className="app-account-btn__org" src={user.org_image_url} alt="" />
      )}
    </span>
    <span>{user?.first_name || 'Account'}</span>
  </button>
);

const StartPage = () => {
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDashboard();
      setAnalyses(response.data.analyses);
      setUser(response.data.user);
      setError(null);
    } catch (err) {
      setError('Failed to load analyses. Please try again.');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalysisDeleted = (deletedAnalysisId) => {
    setAnalyses((prev) => prev.filter((a) => a.id !== deletedAnalysisId));
  };

  return (
    <div className="app-root">
      <nav className="app-nav">
        <div className="app-nav__inner">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Wordmark size={18} />
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ThemeToggle size={34} />
            {!loading && user?.is_staff && (
              <button
                className="app-account-btn"
                title="Application analytics (staff)"
                onClick={() => navigate('/app/application-analytics')}
              >
                <Icon name="activity" size={14} />
                <span>Analytics</span>
              </button>
            )}
            {!loading && user && (
              <AccountButton user={user} onClick={() => navigate('/app/account')} />
            )}
          </div>
        </div>
      </nav>

      <main className="app-main">
        {loading ? (
          <div className="app-state">
            <div className="loading-spinner" />
            <p>Loading your analyses…</p>
          </div>
        ) : error ? (
          <div className="app-state">
            <h2>Something went wrong</h2>
            <p>{error}</p>
            <button className="app-account-btn" onClick={fetchDashboardData}>
              <Icon name="refresh-cw" size={14} />
              <span>Try again</span>
            </button>
          </div>
        ) : (
          <>
            <header className="app-welcome">
              <h1 className="app-welcome__h1">
                Welcome{' '}
                <span className="app-welcome__h1-accent">
                  {user?.first_name ? user.first_name : 'back'}
                </span>
              </h1>
              {/* <p className="app-welcome__sub">
                {analyses.length === 0
                  ? 'Ready to start your first analysis?'
                  : `You have ${analyses.length} analys${analyses.length !== 1 ? 'es' : 'is'}.`}
              </p> */}
            </header>

            <section className="app-section">
              <div className="app-section__head">
                <h2 className="app-section__title">Your analyses</h2>
                <span className="app-section__count">
                  {analyses.length} total
                </span>
              </div>

              <div className="app-analyses-grid">
                <AnalysisCard isNewCard={true} />
                {analyses.map((analysis) => (
                  <AnalysisCard
                    key={analysis.id}
                    analysis={analysis}
                    onAnalysisDeleted={handleAnalysisDeleted}
                  />
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default StartPage;
